/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Locale, LocaleKeys } from '../../../core/locale';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { RootState, useTivoDispatch } from '../../../core/tivo/tivo-redux-wrapper';
import { onCursorStateChangeMyShowsOptions } from '../../../store/settings/userpreferences/myshowsoptionsmenu/myshowsoptionsmenu.navigation';
import { ScreenPreview } from '../../common';
import { Menu } from '../Menu';
import { SettingsMenuTypes } from '../SettingsMenuTypes';
import { SettingsTemplate } from '../SettingsTemplate';

const MyShowsOptions: React.FC = () => {
    const dispatch = useTivoDispatch();

    const {
        settingsOptions: myShowsOptions,
        settingsValues: chosenOption,
        focusedOptionIndex,
        isFocused,
    } = useSelector((state: RootState) => state.myShowsUserSettings);

    const selectedOptionIndex = myShowsOptions?.myShowsOrders.findIndex(
        (option) => option.value.orderBy === chosenOption?.orderBy,
    );

    const previewMap = useMemo(
        () => ({
            byDate: Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS_SORT_BY_DATE),
            byName: Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS_SORT_BY_NAME),
            default: undefined,
        }),
        [],
    );

    const focusedOptionOrderBy =
        focusedOptionIndex !== undefined
            ? myShowsOptions?.myShowsOrders[focusedOptionIndex].value.orderBy
            : undefined;

    const optionPreview = previewMap[focusedOptionOrderBy ?? 'default'];

    const menuItems = myShowsOptions?.myShowsOrders.map((showOrder) => showOrder.label) as string[];

    const onCursorStateChangeCallback = () => {
        dispatch(onCursorStateChangeMyShowsOptions());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    return (
        <SettingsTemplate title={Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS)}>
            <div className="settings-common-content">
                <p className="description">
                    {Locale.getString(LocaleKeys.SETTINGS_MY_SHOWS_OPTIONS_CHOOSE_HOW_TO_VIEW_TEXT)}
                </p>
                {myShowsOptions && (
                    <div className="settings-menu-container">
                        <Menu
                            isFocused={isFocused}
                            items={menuItems}
                            selectedIndex={focusedOptionIndex}
                            focusedIndex={focusedOptionIndex}
                            tick={selectedOptionIndex}
                            name={SettingsMenuTypes.MENU}
                        />
                    </div>
                )}
                <ScreenPreview>{optionPreview && <span>{optionPreview}</span>}</ScreenPreview>
            </div>
        </SettingsTemplate>
    );
};

export default MyShowsOptions;
